// TOTAL row budgets for paged schema introspection, shared by every engine
// (SLICES.md § S7 item 4, rolled out to pg/mysql/oracle/sqlite by item 8).
//
// These are MEMORY bounds spent across ALL pages of one artefact fetch — NOT
// per-request caps. The per-request cap is `INTROSPECTION_PAGE_MAX_ROWS`
// (5,000, in `introspectionPaging.ts`) and only that one has to respect the
// query cache's `FAST_PATH_MAX_ROWS` windowing threshold; see the "Cap
// constraint" note in `introspectionTruncation.ts`.
//
// They live in one module rather than per fetcher so the five engines cannot
// drift apart on "how much of a catalog are we willing to hold", and so the
// `*_ROW_BUDGET` pin in `introspectionTruncation.test.ts` has exactly one
// declaration site per artefact to check. Changing a value here is a
// deliberate edit that must be mirrored in that pin.
//
// Sizing: the old uniform 50,000-row ceiling clipped the tail of a large
// production catalog outright (ordered by schema/table, so late-alphabet
// tables lost EVERYTHING). 200k relations and 2M columns cover a 200k-column
// database with room to spare; the rest are one row per index column /
// FK column / routine parameter, which is why they sit an order of magnitude
// above the object counts they describe.

/** Relation list (tables + views + matviews + `sqlite_master` rows). */
export const TABLE_ROW_BUDGET = 200_000;

/** One row per column of every relation — the largest artefact by far. */
export const COLUMN_ROW_BUDGET = 2_000_000;

/** CHECK-constraint clause text (mssql `sys.check_constraints`, Oracle `ALL_CONSTRAINTS`). */
export const CHECK_ROW_BUDGET = 500_000;

/** One row per index KEY COLUMN, not per index. */
export const INDEX_ROW_BUDGET = 500_000;

/** One row per FK key column pair, not per constraint. */
export const FK_ROW_BUDGET = 500_000;

/** Routines and their parameters — one row per parameter, plus package members. */
export const ROUTINE_ROW_BUDGET = 500_000;

/** Sequences — one row each, and databases declare few. */
export const SEQUENCE_ROW_BUDGET = 100_000;

/**
 * User-defined types and their members: pg `pg_type` / `pg_enum` rows, Oracle
 * `ALL_TYPES` / `ALL_TYPE_ATTRS` rows. One row per enum label / type
 * attribute, so the budget is sized like the other member-level artefacts.
 */
export const TYPE_ROW_BUDGET = 500_000;
